<template>
  <mapit-dialog
    :dialog="isDialogOpen"
    customId="reject-offer-dialog"
    customWidth="595px"
    class="reject-offer-dialog"
    @close="handleClose">
    <template v-slot:header-text>
      <h3 class="reject-offer-dialog__title">{{ $t('offers.reject-dialog.title') }}</h3>
    </template>
    <template v-slot:body v-if="isDialogOpen">
      <div class="reject-offer-dialog__reasons">
        <label
          v-for="reason in reasons"
          :key="reason.value"
          class="reject-offer-dialog__reason"
          @click="selectedReason = reason.value">
          <img
            class="reject-offer-dialog__reason-icon"
            :src="`/images/offer/reject-reasons/${reason.key}.svg`"
            alt="" />
          <span class="reject-offer-dialog__reason-text">
            {{ $t(`offers.reject-dialog.reasons.${reason.key}`) }}
          </span>
          <input
            type="radio"
            class="reject-offer-dialog__reason-radio"
            :value="reason.value"
            :checked="selectedReason === reason.value"
            :data-cy="`reject-reason-${reason.key}`"
            @change="selectedReason = reason.value" />
        </label>
      </div>

      <v-textarea
        v-model="customReason"
        class="reject-offer-dialog__custom"
        :disabled="!isOtherSelected"
        :placeholder="$t('offers.reject-dialog.other-placeholder')"
        :counter="MAX_CUSTOM_LENGTH"
        :maxlength="MAX_CUSTOM_LENGTH"
        rows="3"
        no-resize
        outlined
        hide-details="auto"
        data-cy="reject-custom-reason" />

      <div class="reject-offer-dialog__actions">
        <v-btn class="btn-gray reject-offer-dialog__action" data-cy="reject-cancel" tabindex="-1" @click="handleClose">
          {{ $t('offers.reject-dialog.cancel') }}
        </v-btn>
        <v-btn
          class="btn-primary reject-offer-dialog__action"
          data-cy="reject-confirm"
          tabindex="-1"
          :loading="loading"
          :disabled="!isValid"
          @click="confirm">
          {{ $t('offers.reject-dialog.confirm') }}
        </v-btn>
      </div>
    </template>
  </mapit-dialog>
</template>
<script lang="ts">
import {RejectionReason} from '@muvio/honda-offers-builder/model'
import {computed, defineComponent, ref, watch} from '@vue/composition-api'

import MapitDialog from '@/components/dialog/MapitDialog.vue'

const MAX_CUSTOM_LENGTH = 200

interface ReasonOption {
  key: string
  value: RejectionReason
}

const reasons: ReasonOption[] = [
  {key: 'price', value: RejectionReason.Price},
  {key: 'financing', value: RejectionReason.Financing},
  {key: 'features', value: RejectionReason.Features},
  {key: 'communication', value: RejectionReason.Communication},
  {key: 'model', value: RejectionReason.Model},
  {key: 'other', value: RejectionReason.Other},
]

export default defineComponent({
  name: 'RejectOfferDialog',
  components: {MapitDialog},
  props: {
    isDialogOpen: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {emit}) {
    const selectedReason = ref<RejectionReason | null>(null)
    const customReason = ref('')

    const isOtherSelected = computed(() => selectedReason.value === RejectionReason.Other)

    const isValid = computed(() => {
      if (!selectedReason.value) return false
      if (isOtherSelected.value) {
        const trimmed = customReason.value.trim()
        return trimmed.length > 0 && customReason.value.length <= MAX_CUSTOM_LENGTH
      }
      return true
    })

    function reset() {
      selectedReason.value = null
      customReason.value = ''
    }

    function handleClose() {
      reset()
      emit('close')
    }

    function confirm() {
      if (!isValid.value || !selectedReason.value) return
      emit('reject', {
        rejectionReason: selectedReason.value,
        rejectionReasonCustom: isOtherSelected.value ? customReason.value.trim() : undefined,
      })
    }

    watch(
      () => props.isDialogOpen,
      isOpen => {
        if (isOpen) reset()
      },
    )

    watch(isOtherSelected, otherSelected => {
      if (!otherSelected) customReason.value = ''
    })

    return {
      MAX_CUSTOM_LENGTH,
      reasons,
      selectedReason,
      customReason,
      isOtherSelected,
      isValid,
      handleClose,
      confirm,
    }
  },
})
</script>
<style lang="scss">
.reject-offer-dialog {
  padding: 20px 32px !important;

  &__title {
    padding-top: 24px;
    text-align: center;
  }

  &__reasons {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 24px 0 8px;
  }

  &__reason {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 4px;
    cursor: pointer;
    text-align: left;
  }

  &__reason-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    object-fit: contain;
  }

  &__reason-text {
    flex: 1;
    text-align: left;
  }

  &__reason-radio {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
    width: 18px;
    height: 18px;
    border: 1.5px solid var(--v-primary-base);
    border-radius: 50%;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;

    &:checked {
      background-color: var(--v-primary-base);
    }

    &:checked::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 1.5px 1.5px 0;
      transform: translate(-50%, -60%) rotate(45deg);
    }
  }

  &__custom {
    margin-top: 8px;
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    padding-top: 16px;
  }

  &__action {
    width: 120px !important;
    height: 46px !important;
  }
}
</style>
